import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { MoneyIcon, TokenKeyIcon } from '@/components/icons'
import { isCreditOnlyUsage } from '@/lib/analyticsFormatters'

interface UsageSummaryCardsProps {
  totalTokens: number | undefined
  totalCostUsd: number | undefined
  spendingCapUsd: number | null | undefined
}

function budgetIndicatorColor(pct: number): string {
  if (pct >= 90) return 'bg-destructive'
  if (pct >= 75) return 'bg-amber-500'
  return 'bg-button'
}

export function UsageSummaryCards({ totalTokens, totalCostUsd, spendingCapUsd }: UsageSummaryCardsProps) {
  const hasData = totalTokens !== undefined && totalCostUsd !== undefined
  const hasCap = spendingCapUsd != null && spendingCapUsd > 0
  const budgetPct = hasCap && totalCostUsd !== undefined
    ? Math.min((totalCostUsd / spendingCapUsd) * 100, 100)
    : 0

  const totalTokensLabel = !hasData
    ? '—'
    : isCreditOnlyUsage(totalTokens, totalCostUsd)
      ? '—'
      : totalTokens.toLocaleString('en-US')

  return (
    <div className="grid grid-cols-2 gap-4 mb-6">
      <Card data-testid="total-token-card" className="rounded-2xl">
        <CardContent className="pt-6 pb-6 flex items-start justify-between">
          <div>
            <p className="text-sm font-medium mb-1">Total Token Used</p>
            <p className="text-[32px] font-semibold">{totalTokensLabel}</p>
          </div>
          <div className="h-16 w-16 rounded-xl bg-border flex items-center justify-center shrink-0">
            <TokenKeyIcon className="h-6 w-6" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="monthly-budget-card" className="rounded-2xl">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <p className="text-sm font-medium mb-1">Monthly Budget</p>
              {hasCap && totalCostUsd !== undefined ? (
                <p className="text-[32px] font-semibold">
                  $ {totalCostUsd.toFixed(2)}
                  <span className="text-base font-normal text-muted-foreground">
                    {' '}
                    /{spendingCapUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">No budget set</p>
              )}
            </div>
            <div className="h-16 w-16 rounded-xl bg-border flex items-center justify-center shrink-0">
              <MoneyIcon className="h-6 w-6" />
            </div>
          </div>
          {hasCap && totalCostUsd !== undefined && (
            <>
              <Progress value={budgetPct} className="h-[4px]" indicatorClassName={budgetIndicatorColor(budgetPct)} />
              <div className="flex justify-between text-sm mt-1.5">
                <span className="text-foreground font-bold">
                  {totalCostUsd.toFixed(2)} /{' '}
                  {spendingCapUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-muted-foreground">{Math.round(budgetPct)}% used</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
